import { type DocumentNode, useMutation } from '@apollo/client';
import { BrandIdKeys } from '@seek/brand-types';
import { SET_DEFAULT_NOTIFICATION_PREFERENCE } from '@seek/hirer-notification-preference';
import type { LocationSuggestion } from '@seek/job-location';
import { extractQuestionnaireId } from '@seek/rr-caja';
import { useLanguage } from '@vocab/react';
import { Stack } from 'braid-design-system';
import { addDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { MAX_AD_AGE_IN_DAYS } from 'src/app/shared/constants/adExpiry';
import { SITE } from 'src/app/shared/constants/configObjectProperties';
import { FORM_DATA_AD_EXPIRY_DATE } from 'src/app/shared/constants/dispatcherId';
import { PAGE_ID_MULTI_LOCATION } from 'src/app/shared/constants/pageUid';
import { PaymentOption } from 'src/app/shared/constants/paymentOptions';
import { SITE_DEFAULT } from 'src/app/shared/constants/site';
import {
  MULTILOCATION_ADD_SUCCEEDED,
  MULTILOCATION_FAILED,
  MULTILOCATION_POSTED_PARTIAL_SUCCEEDED,
  MULTILOCATION_SUBMIT_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import {
  useFeatureToggleData,
  FEATURE_BRANDING,
  FEATURE_PER_UNIT_BILLING,
} from 'src/app/shared/featureToggle';
import { hotJarTrigger } from 'src/app/shared/hotJar/hotJarTrigger';
import { getConfig } from 'src/app/utils/config';
import { isoDateFromDate } from 'src/app/utils/date';
import { formatCurrencyFromCents } from 'src/app/utils/formatCurrency';
import { getProductByIdOrFeatures } from 'src/app/utils/getProductByIdOrFeatures';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import dispatchFormData from 'src/app/v1/state/dispatcher/formData';
import { actionUpdatePaymentOption } from 'src/app/v1/state/modules/formData/paymentOption';
import {
  actionResetCart,
  actionUpdateCartLineItem,
  actionUpdateCartOrderId,
} from 'src/app/v1/state/modules/session/cart';
import { actionUpdateIsJobPosted } from 'src/app/v1/state/modules/session/isJobPosted';
import { actionUpdateIsJobPostedEventPublished } from 'src/app/v1/state/modules/session/isJobPostedEventPublished';
import { actionUpdateJob } from 'src/app/v1/state/modules/session/job';
import { actionUpdateJobOrderId } from 'src/app/v1/state/modules/session/jobOrderId';
import { actionResetProductSet } from 'src/app/v1/state/modules/session/productSet';
import { store } from 'src/app/v1/state/store';
import { FakeErrorComponent } from 'src/app/v1/views/components/FakeErrorComponent';
import { withDesktopEnhancers } from 'src/app/v1/views/enhancers/withDesktopEnhancers';
import { mapCartLineItemToOrderLineItem } from 'src/app/v1/views/hooks/useCreateOrderWithProductSet/mappers';
import { useCreatePendingJob } from 'src/app/v1/views/hooks/useCreatePendingJob';
import { useDraft } from 'src/app/v1/views/hooks/useDraft';
import { useEditConfirmInvoiceOrder } from 'src/app/v1/views/hooks/useEditConfirmInvoiceOrder';
import { useIsApplyViaSeek } from 'src/app/v1/views/hooks/useIsApplyViaSeek';
import { useQuestionnaire } from 'src/app/v1/views/hooks/useQuestionnaire';
import PageLayout from 'src/app/v1/views/layouts/PageLayout';
import { JobLocationFields } from 'src/app/v1/views/pages/MultiLocation/components/JobLocation';
import { Success } from 'src/app/v1/views/pages/MultiLocation/components/Success';
import { multiLocations } from 'src/app/v1/views/pages/MultiLocation/constants/multiLocation';
import type {
  LocationKey,
  LocationObject,
} from 'src/app/v1/views/pages/MultiLocation/type';
import PUBLISH_JOB_POSTED_EVENT from 'src/graphql/dataPlatformEvents';
import {
  type Price,
  type Tax,
  type PublishJobPostedEventInput,
  useCreateOrderMutation,
  useCreateProductSetForMultiLocationMutation,
} from 'src/graphql/generated';
import type { EmployerSiteName } from 'src/types/app';
import type { AppState } from 'src/types/appState';
import { CountryCode } from 'src/types/taxonomy';

import { Intro } from './components/Intro';
import { MultiLocationUnavailable } from './components/MultiLocationUnavailable';
import { RoleInformation } from './components/RoleInformation';
import { ErrorAlert, ErrorAlertOnSuccess } from './components/errorAlert';
import { getQueryParams } from './queryParams';

export const isANZSite = (siteName: EmployerSiteName) => {
  const ANZ_SITES: EmployerSiteName[] = [
    'employer-seek-au',
    'employer-seek-nz',
  ];
  return ANZ_SITES.includes(siteName);
};

export const isANZJobLocation = (jobLocationCountry: string) => {
  const NATION_WHITE_LIST = [
    CountryCode.Australia,
    CountryCode.NewZealand,
  ] as string[];
  return NATION_WHITE_LIST.includes(jobLocationCountry);
};

const MultiLocation = () => {
  const {
    advertiserNation,
    brandId,
    draftId,
    flowType,
    interactionId,
    isPrivateAdvertiser,
    jobId,
    jobLocation,
    jobLocationCountry,
    jobTitle,
    legacyId,
    questionnaireUrl,
    revisionId,
    seekAdvertiserId,
    selectedProductFeatures,
    sourceJobId,
    subClassificationId,
    userId,
  } = useSelector((state: AppState) => ({
    advertiserNation: state.session.hirerAccount.nation,
    brandId: state.formData.brand?.[BrandIdKeys.id],
    draftId: state.session.draftId,
    flowType: state.formData.flowType,
    interactionId: state.session.interactionId,
    isPrivateAdvertiser: state.session.hirerAccount?.isPrivateAdvertiser,
    jobId: state.session.job?.id,
    jobLocation: state.session.jobLocationText,
    jobLocationCountry: state.session.jobLocationCountry,
    jobTitle: state.formData.jobTitle,
    legacyId: state.formData.brand?.[BrandIdKeys.legacyId],
    questionnaireUrl: state.formData.questionnaireUrl,
    revisionId: state.session.job?.revisionId,
    seekAdvertiserId: state.session.seekAdvertiserId,
    selectedProductFeatures: state.session.productFeatures,
    sourceJobId: state.session.sourceJobId,
    subClassificationId: state.session.seekSubClassificationId,
    userId: state.session.userId,
  }));

  const dispatch = useDispatch();
  dispatch(actionUpdatePaymentOption(PaymentOption.INVOICE));

  const nowDate = new Date();
  const maxDateStr = isoDateFromDate(addDays(nowDate, MAX_AD_AGE_IN_DAYS));

  const isBrandingEnabled = useFeatureToggleData(FEATURE_BRANDING);

  const [location, setLocation] = useState<LocationObject>(
    JSON.parse(JSON.stringify(multiLocations)),
  );
  const [locationCount, setLocationCount] = useState(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorLocations, setErrorLocations] = useState<
    Array<string | undefined>
  >([]);
  const [requestValidation, setRequestValidation] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const { locale, language } = useLanguage();

  const site = getConfig(SITE, SITE_DEFAULT) as unknown as EmployerSiteName;
  const { search } = useLocation();
  const decodedQS = getQueryParams(search);

  useEffect(() => {
    if (jobId && revisionId) {
      dispatch(
        actionUpdateCartLineItem({
          type: 'AdProduct',
          productSetId: '',
          productId: '',
          jobId: jobId ?? '',
          revisionId: revisionId ?? '',
        }),
      );
    }
  }, [dispatch, jobId, revisionId]);

  const { createDraft, saveDraft, updateDraft } = useDraft();

  const confirmInvoiceOrder = useEditConfirmInvoiceOrder();

  const createPendingJob = useCreatePendingJob({
    advertiserNation,
    interactionId,
  });

  const [createOrderMutation] = useCreateOrderMutation();

  const [createProductSetForMultiLocation] =
    useCreateProductSetForMultiLocationMutation();

  const [setDefaultNotificationPreference] = useMutation(
    SET_DEFAULT_NOTIFICATION_PREFERENCE as DocumentNode,
    {
      variables: {
        input: {
          jobId: String(jobId),
        },
      },
      onError: (error) => {
        log.error('MultiLocation | SetDefaultPreference', {
          error,
          jobId,
          seekAdvertiserId,
        });
      },
    },
  );

  const [sendJobPublishedEvent] = useMutation<PublishJobPostedEventInput>(
    PUBLISH_JOB_POSTED_EVENT,
    {
      variables: {
        input: {
          adcentreUserId: Number(userId),
          draftId,
          adcentreListingId: Number(jobId),
          advertiserId: seekAdvertiserId,
          device: 'DESKTOP',
          experimentId: '',
          experimentVariantId: '',
          flowType,
          jobId: String(jobId),
          jobPostingExperience: 'EXPRESS',
          jobWritingFlow: 'STANDARD',
        },
      },
      onCompleted: () => {
        dispatch(actionUpdateIsJobPostedEventPublished(true));
      },
      onError: (error) => {
        log.error('MultiLocation | SendJobPublishedEvent', {
          error,
          jobId,
          seekAdvertiserId,
        });
      },
    },
  );

  const setQuestionnaire = useQuestionnaire();

  const applyViaSeek = useIsApplyViaSeek();

  const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);

  const getJobPriceFromProduct = (selectedProduct: any) => {
    const netPrice = (selectedProduct?.price as Price)?.netPrice?.value;
    if (!netPrice) {
      log.error(
        'MultiLocation | getJobPriceFromProductSet | Unable to load price',
        {
          selectedProductId: selectedProduct?.id,
          jobId,
          seekAdvertiserId,
          sourceJobId,
        },
      );
      return undefined;
    }
    return formatCurrencyFromCents(netPrice);
  };

  const getTaxFromProduct = (selectedProduct: any) => {
    const tax = (selectedProduct?.price?.tax as Tax)?.amount?.value;
    if (!tax) {
      log.error('MultiLocation | getTaxFromProduct | Unable to get tax', {
        selectedProductId: selectedProduct?.id,
        jobId,
        seekAdvertiserId,
        sourceJobId,
      });
      return undefined;
    }
    return formatCurrencyFromCents(tax);
  };

  const handleOnSuggestionSelected = async (
    id: LocationKey,
    suggestion: LocationSuggestion,
  ) => {
    const newLocation = { ...location };
    if (
      suggestion.isValid &&
      suggestion.location.value !== newLocation[id].jobLocationId
    ) {
      newLocation[id].jobLocationId = suggestion.location.value;
      newLocation[id].jobLocationText = suggestion.location.text;

      const response = await createProductSetForMultiLocation({
        variables: {
          input: {
            advertiserId: seekAdvertiserId,
            jobAdDetails: {
              locationId: location[id].jobLocationId ?? '',
              subClassificationId,
              roleTitle: jobTitle,
            },
            jobId: sourceJobId ?? '',
            locale: locale || language,
          },
        },
        onError: (error) => {
          log.error(
            'MultiLocation | HandleOnSuggestion | CreateProductSetForMultiLocation',
            {
              error,
              sourceJobId,
              seekAdvertiserId,
              subClassificationId,
              jobTitle,
            },
          );
        },
      });
      if (response.data?.createProductSetForMultiLocation.productSet) {
        const newProductSet =
          response.data?.createProductSetForMultiLocation.productSet;
        location[id].productSet = newProductSet;

        const selectedProduct = getProductByIdOrFeatures({
          productSet: newProductSet,
          selectedProductFeatures,
        });

        location[id].selectedProductId = selectedProduct?.id;
        location[id].jobPrice = getJobPriceFromProduct(selectedProduct) ?? '';
        location[id].tax = getTaxFromProduct(selectedProduct) ?? '';
      }

      setLocation(newLocation);
    }
  };

  const updateIsErrorJobPosting = (locationId: LocationKey) => {
    const newLocations = { ...location };
    newLocations[locationId].isErrorWhenPosting = true;
    setLocation(newLocations);
  };

  const handleButtonClick = async () => {
    getTrackingAdapter.sendLinkEvent({
      eventName: MULTILOCATION_SUBMIT_PRESSED,
    });
    let isValid = true;
    let k: keyof typeof location;

    setRequestValidation(true);

    dispatch(dispatchFormData(FORM_DATA_AD_EXPIRY_DATE, new Date(maxDateStr)));

    for (let i = 1; i <= locationCount; i++) {
      if (
        !Boolean(location[`location${i}` as LocationKey].jobLocationText) ||
        !Boolean(location[`location${i}` as LocationKey].jobLocationId)
      ) {
        isValid = false;
      }
    }

    // Loops through all the locations - for any error it will update location object
    if (isValid) {
      const sourceQuestionnaireId = extractQuestionnaireId(questionnaireUrl);

      for (k in location) {
        if (location.hasOwnProperty(k)) {
          const currentLocation = location[k];
          if (currentLocation.jobLocationId) {
            dispatch(actionResetCart());
            dispatch(actionResetProductSet);

            setIsLoading(true);

            await setQuestionnaire(sourceQuestionnaireId);

            const newDraftId = await createDraft({
              jobLocationId: currentLocation.jobLocationId,
              selectedProductId: currentLocation.selectedProductId,
              productSet: currentLocation.productSet,
            });

            if (!newDraftId) {
              updateIsErrorJobPosting(k);
              continue;
            }
            // create product set
            let response;
            try {
              response = await createProductSetForMultiLocation({
                variables: {
                  input: {
                    advertiserId: seekAdvertiserId,
                    jobAdDetails: {
                      locationId: currentLocation.jobLocationId ?? '',
                      subClassificationId,
                      roleTitle: jobTitle,
                    },
                    draftId: newDraftId,
                    jobId: sourceJobId ?? '',
                  },
                },
              });
            } catch (error) {
              log.error(
                'MultiLocation | HandleButtonClick | CreateProductSetForMultiLocation',
                {
                  error,
                  seekAdvertiserId,
                  subClassificationId,
                  jobTitle,
                },
              );
              updateIsErrorJobPosting(k);
              continue;
            }

            const newProductSet =
              response.data?.createProductSetForMultiLocation.productSet;
            if (Boolean(newProductSet)) {
              currentLocation.productSet = newProductSet;
              currentLocation.selectedProductId = getProductByIdOrFeatures({
                productSet: newProductSet,
                selectedProductFeatures,
              })?.id;

              await updateDraft(false, {
                jobLocationId: currentLocation.jobLocationId,
                productSet: currentLocation.productSet,
                selectedProductId: currentLocation.selectedProductId,
              });

              // Create pending job
              const branding =
                isBrandingEnabled && brandId && !isPrivateAdvertiser
                  ? { brandId, legacyId }
                  : undefined;
              const idempotencyKey = uuidv4();
              const { data: jobData } = await createPendingJob({
                variables: {
                  input: {
                    advertiserId: seekAdvertiserId,
                    draftId: newDraftId,
                    branding,
                    idempotencyKey,
                  },
                },
                onError: (error) => {
                  log.error('MultiLocation | CreatePendingJob', {
                    error,
                    seekAdvertiserId,
                    idempotencyKey,
                  });
                },
              });

              if (!jobData?.createJob) {
                updateIsErrorJobPosting(k);
                continue;
              } else {
                dispatch(
                  actionUpdateJob({
                    id: jobData.createJob.jobId,
                    idempotencyKey,
                    revisionId: jobData.createJob.revisionId,
                  }),
                );
              }

              const {
                session: {
                  cart: { lineItems: cartLineItems },
                },
              } = store.getState();

              let orderId: string;

              // Create Order with line items
              try {
                const { data } = await createOrderMutation({
                  context: {
                    headers: {
                      site: advertiserNation,
                      'interaction-id': interactionId,
                    },
                  },
                  variables: {
                    input: {
                      advertiserId: seekAdvertiserId,
                      advertiserNation,
                      lineItems: cartLineItems.map((cartItem: any) =>
                        mapCartLineItemToOrderLineItem({
                          ...cartItem,
                          productId: currentLocation.selectedProductId,
                          productSetId: currentLocation.productSet?.id,
                        }),
                      ),
                      orderCreationId: uuidv4(),
                    },
                  },
                });
                orderId = data?.createOrder?.order?.orderId ?? '';
              } catch (error) {
                log.error('MultiLocation | HandleButtonClick | CreateOrder', {
                  error,
                  seekAdvertiserId,
                  jobId,
                });
                updateIsErrorJobPosting(k);
                continue;
              }

              if (Boolean(orderId)) {
                dispatch(actionUpdateCartOrderId(orderId ?? null));
                dispatch(actionUpdateJobOrderId(orderId ?? ''));
                try {
                  await confirmInvoiceOrder(idempotencyKey, orderId);
                } catch (error) {
                  log.error('MultiLocation | ConfirmInvoiceOrder', {
                    error,
                    seekAdvertiserId,
                    orderId,
                    idempotencyKey,
                  });
                  updateIsErrorJobPosting(k);
                  continue;
                }
                try {
                  await saveDraft(true, true, {
                    jobLocationId: currentLocation.jobLocationId,
                    productSet: currentLocation.productSet,
                    selectedProductId: currentLocation.selectedProductId,
                  });
                } catch (error) {
                  log.error('MultiLocation | SaveDraft', {
                    error,
                    seekAdvertiserId,
                    draftId: newDraftId,
                  });
                  updateIsErrorJobPosting(k);
                  continue;
                }

                if (applyViaSeek) {
                  await setDefaultNotificationPreference();
                }

                await sendJobPublishedEvent();

                dispatch(actionUpdateIsJobPosted(true));
              }
            }
          }
        }
      }
    }
    // For any job posting error, show error panel
    let locationsFailedCount = 0;
    const errors = [];
    const successLocations = [];
    let productTotalPrice = 0;
    for (k in location) {
      if (location.hasOwnProperty(k)) {
        const currentLocation = location[k];
        if (Boolean(currentLocation.jobPrice)) {
          productTotalPrice =
            productTotalPrice +
            Number(currentLocation.jobPrice?.replace('$', ''));
        }

        if (currentLocation.isErrorWhenPosting) {
          locationsFailedCount = locationsFailedCount + 1;
          errors.push(currentLocation.jobLocationText);
        } else if (Boolean(currentLocation.jobLocationText)) {
          successLocations.push(currentLocation.jobLocationText);
        }
      }
    }

    if (locationsFailedCount === locationCount) {
      getTrackingAdapter.sendLinkEvent({
        eventName: MULTILOCATION_FAILED,
      });

      setIsLoading(false);
      setShowErrorAlert(true);
    } else if (isValid) {
      if (locationsFailedCount === 0) {
        getTrackingAdapter.sendLinkEvent({
          numberOfAdsPosted: locationCount,
          successLocations,
          successLocationsCount: successLocations.length,
          productTotalPrice,
          eventName: MULTILOCATION_ADD_SUCCEEDED,
        });
      } else {
        getTrackingAdapter.sendLinkEvent({
          numberOfAdsPosted: locationCount,
          failedLocations: errors,
          successLocations,
          successLocationsCount: successLocations.length,
          failedLocationsCount: errors.length,
          productTotalPrice,
          eventName: MULTILOCATION_POSTED_PARTIAL_SUCCEEDED,
        });
      }

      hotJarTrigger('multilocation-success');

      setIsLoading(false);
      setErrorLocations(errors);
      setIsSuccessful(true);
    }
  };

  if (!decodedQS.jobId || !jobLocationCountry) {
    return null;
  }

  const isAnzHirerWithAnzJobLocation =
    isANZSite(site) && isANZJobLocation(jobLocationCountry);
  if (isAnzHirerWithAnzJobLocation === false) {
    return (
      <MultiLocationUnavailable jobId={decodedQS.jobId as string | undefined} />
    );
  }

  if (isPerUnitBilling) {
    return (
      <MultiLocationUnavailable jobId={decodedQS.jobId as string | undefined} />
    );
  }

  return (
    <PageLayout hasAside hasProgressBar={false}>
      <Stack space="large">
        {isSuccessful && !showErrorAlert ? (
          <Success errorLocations={errorLocations} />
        ) : (
          <>
            <Intro />
            <RoleInformation
              jobTitle={jobTitle}
              jobLocation={jobLocation}
              loading={false}
            />
            <JobLocationFields
              handleOnSuggestionSelected={handleOnSuggestionSelected}
              handleButtonClick={handleButtonClick}
              requestValidation={requestValidation}
              setRequestValidation={setRequestValidation}
              isLoading={isLoading}
              locations={location}
              setLocations={setLocation}
              locationCount={locationCount}
              setLocationCount={setLocationCount}
            />
            {showErrorAlert && <ErrorAlert />}
          </>
        )}
      </Stack>
      <FakeErrorComponent>
        <ErrorAlert />
        <ErrorAlertOnSuccess />
      </FakeErrorComponent>
    </PageLayout>
  );
};

export const MultiLocationPage = withDesktopEnhancers(PAGE_ID_MULTI_LOCATION)(
  MultiLocation,
);
